import {
  AddressField,
  AllergiesField,
  AppointmentBookingField,
  BelugaPatientPreferenceField,
  ConditionsField,
  DatabaseSelectField,
  DateField,
  DateStringField,
  DropdownField,
  EmailField,
  FileField,
  HeightField,
  InputTableField,
  InsuranceField,
  MedicationsField,
  MultipleChoiceField,
  NumberField,
  PhoneField,
  RankingField,
  RatingField,
  RedirectField,
  RelatedContactsField,
  RichTextField,
  SignatureField,
  StringField,
  StringLongField,
  StripeField,
  TimeField,
  TimezoneField,
} from "./fields";
import type { FormTypesWithValues, TellescopeFormFieldPropsMap } from "./types";

export type TellescopeFormFieldProps<T extends FormTypesWithValues> = TellescopeFormFieldPropsMap[T];
type Rest<T extends FormTypesWithValues> = Omit<
  TellescopeFormFieldProps<T>,
  "type" | "value" | "onChange" | "className"
>;

export const TellescopeFormField = <T extends FormTypesWithValues>({
  type,
  value,
  onChange,
  className,
  ...rest
}: TellescopeFormFieldProps<T>) => {
  switch (type) {
    case "Address":
      return <AddressField type={type} value={value} onChange={onChange} className={className} {...rest} />;
    case "Allergies":
      return <AllergiesField type={type} value={value} onChange={onChange} className={className} {...rest} />;
    case "Appointment Booking": {
      const typedRest = rest as unknown as Rest<"Appointment Booking">;
      return (
        <AppointmentBookingField type={type} value={value} onChange={onChange} className={className} {...typedRest} />
      );
    }
    case "Beluga Patient Preference":
      return (
        <BelugaPatientPreferenceField type={type} value={value} onChange={onChange} className={className} {...rest} />
      );
    case "Conditions":
      return <ConditionsField type={type} value={value} onChange={onChange} className={className} {...rest} />;
    case "Database Select":
      return <DatabaseSelectField type={type} value={value} onChange={onChange} className={className} {...rest} />;
    case "date":
      return <DateField type={type} value={value} onChange={onChange} className={className} {...rest} />;
    case "dateString":
      return <DateStringField type={type} value={value} onChange={onChange} className={className} {...rest} />;
    case "Dropdown":
      return <DropdownField type={type} value={value} onChange={onChange} className={className} {...rest} />;
    case "email":
      return <EmailField type={type} value={value} onChange={onChange} className={className} {...rest} />;
    case "file": {
      const typedRest = rest as unknown as Rest<"file">;
      return (
        <FileField
          type="files"
          value={value === undefined ? [] : [value]}
          onChange={(file) => onChange(file?.[0])}
          className={className}
          {...typedRest}
        />
      );
    }

    case "files": {
      const typedRest = rest as unknown as Rest<"files">;
      return <FileField multiple type={type} value={value} onChange={onChange} className={className} {...typedRest} />;
    }
    case "Height":
      return <HeightField type={type} value={value} onChange={onChange} className={className} {...rest} />;
    case "Hidden Value":
      return null;
    case "Input Table":
      return <InputTableField type={type} value={value} onChange={onChange} className={className} {...rest} />;
    case "Insurance":
      return <InsuranceField type={type} value={value} onChange={onChange} className={className} {...rest} />;
    case "Medications":
      return <MedicationsField type={type} value={value} onChange={onChange} className={className} {...rest} />;
    case "multiple_choice":
      return <MultipleChoiceField type={type} value={value} onChange={onChange} className={className} {...rest} />;
    case "number":
      return <NumberField type={type} value={value} onChange={onChange} className={className} {...rest} />;
    case "phone":
      return <PhoneField type={type} value={value} onChange={onChange} className={className} {...rest} />;
    case "Redirect":
      return <RedirectField type={type} value={value} onChange={onChange} className={className} {...rest} />;
    case "signature": {
      const typedRest = rest as unknown as Rest<"signature">;
      return <SignatureField type={type} value={value} onChange={onChange} className={className} {...typedRest} />;
    }
    case "string":
      return <StringField type={type} value={value} onChange={onChange} className={className} {...rest} />;
    case "stringLong":
      return <StringLongField type={type} value={value} onChange={onChange} className={className} {...rest} />;
    case "Timezone":
      return <TimezoneField type={type} value={value} onChange={onChange} className={className} {...rest} />;
    case "ranking":
      return <RankingField type={type} value={value} onChange={onChange} className={className} {...rest} />;
    case "rating":
      return <RatingField type={type} value={value} onChange={onChange} className={className} {...rest} />;
    case "Related Contacts": {
      const typedRest = rest as unknown as Rest<"Related Contacts">;
      return (
        <RelatedContactsField type={type} value={value} onChange={onChange} className={className} {...typedRest} />
      );
    }
    case "Rich Text":
      return <RichTextField type={type} value={value} onChange={onChange} className={className} {...rest} />;
    case "Stripe": {
      const typedRest = rest as unknown as Rest<"Stripe">;
      return <StripeField type={type} value={value} onChange={onChange} className={className} {...typedRest} />;
    }
    case "Time":
      return <TimeField type={type} value={value} onChange={onChange} className={className} {...rest} />;
    case "Bridge Eligibility":
      // Placeholder for Bridge Eligibility field - not yet implemented in portal
      return null;
    case "Pharmacy Search":
      // Placeholder for Pharmacy Search field - not yet implemented in portal
      return null;
    case "Candid Eligibility":
      // Placeholder for Candid Eligibility field - not yet implemented in portal
      return null;
    default: {
      const _exhaustiveType: undefined | never = type;
      return null;
    }
  }
};
